/**
 * Border/frame catalogue for the notice generator.
 *
 * Each frame is an SVG document built for the exact pixel size of the notice,
 * so line weights and ornaments are authored in a single reference unit (`u`,
 * one hundredth of the short edge) and come out proportional on every paper
 * preset — an A4 landscape sheet and a square sticker get the same-looking rule.
 *
 * The frame is drawn as its own layer (background first, frame, then text and
 * icon), which is why every builder returns markup rather than drawing calls:
 * the preview and the export both rasterise the same string.
 *
 * `inset` is the fraction of the short edge the content must keep clear of the
 * paper edge — the layout engine reads it so text never collides with the rule.
 */

/* ------------------------------------------------------------------ *
 *  Drawing helpers
 *
 *  Everything below is expressed in `u` (1% of the short edge) by the callers,
 *  so a frame reads as "a 1.6-unit rule 3.5 units in" rather than as pixels.
 * ------------------------------------------------------------------ */

/** Concentric stroked rectangle, `pad` units in from every edge. */
function rule(w, h, u, color, pad, weight, extra = '') {
    const p = pad * u;
    return `<rect x="${p}" y="${p}" width="${w - p * 2}" height="${h - p * 2}"
        fill="none" stroke="${color}" stroke-width="${weight * u}" ${extra}/>`;
}

/** The four corner points of a rectangle inset by `pad`. */
function corners(w, h, u, pad) {
    const p = pad * u;
    return [[p, p], [w - p, p], [p, h - p], [w - p, h - p]];
}

/**
 * Zig-zag between two points, `amp` units to either side of the line.
 * Direction-agnostic: the offset is taken along the edge's normal, so the same
 * helper draws the top, bottom and both sides of a frame.
 */
function zigzagLine(x1, y1, x2, y2, amp, step) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;
    const count = Math.max(2, Math.round(len / step));
    let d = `M${x1} ${y1}`;
    for (let i = 1; i <= count; i += 1) {
        const t = (i / count) * len;
        const side = i % 2 ? 1 : -1;
        // The last vertex lands back on the line so the four edges meet cleanly.
        const a = i === count ? 0 : amp * side;
        d += ` L${x1 + ux * t + nx * a} ${y1 + uy * t + ny * a}`;
    }
    return d;
}

/** Sine-ish wave between two points, drawn as alternating quadratic arcs. */
function waveLine(x1, y1, x2, y2, amp, step) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;
    const count = Math.max(2, Math.round(len / step));
    let d = `M${x1} ${y1}`;
    for (let i = 1; i <= count; i += 1) {
        const t = (i / count) * len;
        const mid = t - (len / count) / 2;
        const side = i % 2 ? 1 : -1;
        d += ` Q${x1 + ux * mid + nx * amp * side} ${y1 + uy * mid + ny * amp * side}`
            + ` ${x1 + ux * t} ${y1 + uy * t}`;
    }
    return d;
}

/** Scalloped edge — a run of half-circles bulging outward. */
function scallopLine(x1, y1, x2, y2, radius) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const count = Math.max(2, Math.round(len / (radius * 2)));
    const stepLen = len / count;
    const ux = dx / len;
    const uy = dy / len;
    let d = `M${x1} ${y1}`;
    for (let i = 1; i <= count; i += 1) {
        const t = (i / count) * len;
        d += ` A${stepLen / 2} ${stepLen / 2} 0 0 1 ${x1 + ux * t} ${y1 + uy * t}`;
    }
    return d;
}

/** Run an edge builder around all four sides of a rectangle. */
function aroundBox(w, h, u, pad, build) {
    const p = pad * u;
    const x2 = w - p;
    const y2 = h - p;
    return [
        build(p, p, x2, p),
        build(x2, p, x2, y2),
        build(x2, y2, p, y2),
        build(p, y2, p, p),
    ].join(' ');
}

/**
 * Ornamental corner scroll, authored in a 100×100 top-left corner box.
 * Geometry stays in that box and the caller scales it with a transform, so the
 * stroke weight is written in box units and scales with the motif.
 */
function ornateCorner(color) {
    return `
        <g fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round">
            <path d="M4 96 V40 C4 20 20 4 40 4 H96"/>
            <path d="M18 96 V44 C18 30 30 18 44 18 H96"/>
            <path d="M32 54 C32 40 40 32 54 32 C64 32 70 38 70 46 C70 53 65 58 58 58 C52 58 48 54 48 49 C48 45 51 42 55 42"/>
            <path d="M4 64 C16 64 22 58 22 46"/>
            <path d="M64 4 C64 16 58 22 46 22"/>
        </g>
    `;
}

/** Leafy corner motif — softer than the scroll, same 100×100 box. */
function floralCorner(color) {
    return `
        <g fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round">
            <path d="M6 96 V44 C6 22 22 6 44 6 H96"/>
            <path d="M20 70 C34 70 44 60 44 46 C58 46 68 36 68 22"/>
            <path d="M20 70 C20 56 28 48 42 47"/>
            <path d="M68 22 C54 23 46 31 45 45"/>
            <path d="M30 88 C44 88 52 80 52 66"/>
            <path d="M88 30 C88 44 80 52 66 52"/>
        </g>
    `;
}

/** Art-deco stepped corner — straight lines only, in the same 100×100 box. */
function decoCorner(color) {
    return `
        <g fill="none" stroke="${color}" stroke-width="3.4" stroke-linecap="square">
            <path d="M4 96 V26 H26 V4 H96"/>
            <path d="M20 96 V40 H40 V20 H96"/>
            <path d="M36 96 V54 H54 V36 H96"/>
        </g>
    `;
}

/** Small centred flourish for the middle of a rule, in a 100×40 box. */
function ornateSprig(color) {
    return `
        <g fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round">
            <path d="M2 20 H26"/>
            <path d="M98 20 H74"/>
            <path d="M50 22 C42 10 30 12 28 20 C27 27 36 30 43 25"/>
            <path d="M50 22 C58 10 70 12 72 20 C73 27 64 30 57 25"/>
            <circle cx="50" cy="24" r="5"/>
        </g>
    `;
}

/** Laurel half-wreath for the middle of a side rule, in a 100×40 box. */
function laurelSprig(color) {
    return `
        <g fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round">
            <path d="M8 20 H92"/>
            <path d="M26 20 C26 10 34 6 42 8"/>
            <path d="M38 20 C38 10 46 6 54 8"/>
            <path d="M50 20 C50 10 58 6 66 8"/>
            <path d="M62 20 C62 10 70 6 78 8"/>
        </g>
    `;
}

/** Place a 100-unit corner motif at all four corners, mirrored into place. */
function fourCorners(w, h, u, motif, size = 24, pad = 2.5) {
    const k = (size * u) / 100;
    const p = pad * u;
    return `
        <g transform="translate(${p} ${p}) scale(${k})">${motif}</g>
        <g transform="translate(${w - p} ${p}) scale(${-k} ${k})">${motif}</g>
        <g transform="translate(${p} ${h - p}) scale(${k} ${-k})">${motif}</g>
        <g transform="translate(${w - p} ${h - p}) scale(${-k} ${-k})">${motif}</g>
    `;
}

/** Place a 100×40 sprig centred on the top and bottom rules. */
function topBottomSprigs(w, h, u, motif, size = 24, pad = 2.5) {
    const k = (size * u) / 100;
    const p = pad * u;
    const x = (w - size * u) / 2;
    return `
        <g transform="translate(${x} ${p}) scale(${k})">${motif}</g>
        <g transform="translate(${x} ${h - p}) scale(${k} ${-k})">${motif}</g>
    `;
}

/* ------------------------------------------------------------------ *
 *  Catalogue
 * ------------------------------------------------------------------ */

export const NOTICE_FRAMES = [
    { id: 'none', inset: 0.06, build: () => '' },

    /* — plain rules ------------------------------------------------- */
    {
        id: 'hairline',
        inset: 0.10,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 0.35),
    },
    {
        id: 'single',
        inset: 0.11,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 1.1),
    },
    {
        id: 'bold',
        inset: 0.13,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 2.8),
    },
    {
        id: 'double',
        inset: 0.13,
        build: (w, h, u, c) => rule(w, h, u, c, 3.5, 1.6) + rule(w, h, u, c, 6.5, 0.55),
    },
    {
        id: 'triple',
        inset: 0.15,
        build: (w, h, u, c) => rule(w, h, u, c, 3, 1.5) + rule(w, h, u, c, 5.6, 0.5) + rule(w, h, u, c, 7.6, 0.5),
    },
    {
        id: 'thickThin',
        inset: 0.14,
        build: (w, h, u, c) => rule(w, h, u, c, 3, 2.6) + rule(w, h, u, c, 7.5, 0.5),
    },
    {
        id: 'inline',
        inset: 0.14,
        build: (w, h, u, c) => rule(w, h, u, c, 3.5, 0.5) + rule(w, h, u, c, 5.2, 1.9) + rule(w, h, u, c, 7, 0.5),
    },

    /* — rounded ----------------------------------------------------- */
    {
        id: 'rounded',
        inset: 0.12,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 1.4, `rx="${5 * u}"`),
    },
    {
        id: 'roundedDouble',
        inset: 0.13,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 3.5, 1.5, `rx="${5 * u}"`) + rule(w, h, u, c, 6.5, 0.5, `rx="${3 * u}"`),
    },
    {
        id: 'pill',
        inset: 0.15,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 1.5, `rx="${14 * u}"`),
    },

    /* — broken rules ------------------------------------------------ */
    {
        id: 'dashed',
        inset: 0.12,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 1.2, `stroke-dasharray="${4 * u} ${2.4 * u}"`),
    },
    {
        id: 'dotted',
        inset: 0.12,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 4, 1.3, `stroke-linecap="round" stroke-dasharray="0.01 ${3 * u}"`),
    },
    {
        id: 'dashDot',
        inset: 0.12,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 4, 1.2, `stroke-linecap="round" stroke-dasharray="${5 * u} ${2 * u} 0.01 ${2 * u}"`),
    },
    {
        id: 'stitch',
        inset: 0.14,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 3, 1.6) + rule(w, h, u, c, 6.5, 0.8, `stroke-dasharray="${2.6 * u} ${1.8 * u}"`),
    },

    /* — corners ----------------------------------------------------- */
    {
        id: 'brackets',
        inset: 0.11,
        build: (w, h, u, c) => {
            const p = 4 * u;
            const len = 14 * u;
            const line = (x1, y1, x2, y2) =>
                `<path d="M${x1} ${y1} L${x2} ${y2}" stroke="${c}" stroke-width="${1.8 * u}" stroke-linecap="round" fill="none"/>`;
            return [
                line(p, p + len, p, p), line(p, p, p + len, p),
                line(w - p - len, p, w - p, p), line(w - p, p, w - p, p + len),
                line(p, h - p - len, p, h - p), line(p, h - p, p + len, h - p),
                line(w - p - len, h - p, w - p, h - p), line(w - p, h - p, w - p, h - p - len),
            ].join('');
        },
    },
    {
        id: 'studded',
        inset: 0.14,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 3.5, 1.4)
            + rule(w, h, u, c, 6, 0.5)
            + corners(w, h, u, 3.5).map(([x, y]) => `<circle cx="${x}" cy="${y}" r="${1.5 * u}" fill="${c}"/>`).join(''),
    },
    {
        id: 'cornerBlocks',
        inset: 0.14,
        build: (w, h, u, c) => {
            const s = 5 * u;
            return rule(w, h, u, c, 4, 0.9)
                + corners(w, h, u, 4)
                    .map(([x, y]) => `<rect x="${x - s / 2}" y="${y - s / 2}" width="${s}" height="${s}" fill="${c}"/>`)
                    .join('');
        },
    },
    {
        id: 'cornerDiamonds',
        inset: 0.15,
        build: (w, h, u, c) => {
            const s = 3.4 * u;
            return rule(w, h, u, c, 3.5, 1.4)
                + rule(w, h, u, c, 6.5, 0.5)
                + corners(w, h, u, 5)
                    .map(([x, y]) => `<rect x="${x - s / 2}" y="${y - s / 2}" width="${s}" height="${s}" fill="${c}" transform="rotate(45 ${x} ${y})"/>`)
                    .join('');
        },
    },
    {
        id: 'chamfer',
        inset: 0.14,
        build: (w, h, u, c) => {
            const p = 4 * u;
            const k = 9 * u;
            const d = `M${p + k} ${p} H${w - p - k} L${w - p} ${p + k} V${h - p - k} L${w - p - k} ${h - p}`
                + ` H${p + k} L${p} ${h - p - k} V${p + k} Z`;
            return `<path d="${d}" fill="none" stroke="${c}" stroke-width="${1.6 * u}"/>`;
        },
    },
    {
        id: 'notched',
        inset: 0.14,
        build: (w, h, u, c) => {
            const k = 7 * u;
            return rule(w, h, u, c, 4, 1.3)
                + corners(w, h, u, 4)
                    .map(([x, y], i) => {
                        const sx = i % 2 ? -1 : 1;
                        const sy = i < 2 ? 1 : -1;
                        return `<path d="M${x + sx * k} ${y} L${x} ${y + sy * k}" stroke="${c}" stroke-width="${1.3 * u}" fill="none"/>`;
                    })
                    .join('');
        },
    },
    {
        id: 'crosshair',
        inset: 0.14,
        build: (w, h, u, c) => {
            const k = 5 * u;
            return rule(w, h, u, c, 4, 0.9)
                + corners(w, h, u, 4)
                    .map(([x, y]) => `<path d="M${x - k} ${y} H${x + k} M${x} ${y - k} V${y + k}" stroke="${c}" stroke-width="${0.9 * u}" fill="none"/>`)
                    .join('');
        },
    },

    /* — bands and bars ---------------------------------------------- */
    {
        id: 'band',
        inset: 0.14,
        build: (w, h, u, c) => `
            <rect x="0" y="0" width="${w}" height="${5 * u}" fill="${c}"/>
            <rect x="0" y="${h - 5 * u}" width="${w}" height="${5 * u}" fill="${c}"/>
            ${rule(w, h, u, c, 7, 0.5)}
        `,
    },
    {
        id: 'headerBand',
        inset: 0.14,
        build: (w, h, u, c) => `
            <rect x="0" y="0" width="${w}" height="${7 * u}" fill="${c}"/>
            ${rule(w, h, u, c, 3.5, 1.2)}
        `,
    },
    {
        id: 'sideBars',
        inset: 0.15,
        build: (w, h, u, c) => `
            <rect x="0" y="0" width="${5 * u}" height="${h}" fill="${c}"/>
            <rect x="${w - 5 * u}" y="0" width="${5 * u}" height="${h}" fill="${c}"/>
            ${rule(w, h, u, c, 8, 0.5)}
        `,
    },
    {
        id: 'ticket',
        inset: 0.15,
        build: (w, h, u, c) => `
            ${rule(w, h, u, c, 4, 1.2, `stroke-dasharray="${3.4 * u} ${2.2 * u}"`)}
            <circle cx="0" cy="${h / 2}" r="${4 * u}" fill="none" stroke="${c}" stroke-width="${1.2 * u}"/>
            <circle cx="${w}" cy="${h / 2}" r="${4 * u}" fill="none" stroke="${c}" stroke-width="${1.2 * u}"/>
        `,
    },
    {
        id: 'ticks',
        inset: 0.14,
        build: (w, h, u, c) => {
            const p = 4 * u;
            const k = 3.2 * u;
            const mids = [[w / 2, p, 0, 1], [w / 2, h - p, 0, -1], [p, h / 2, 1, 0], [w - p, h / 2, -1, 0]];
            return rule(w, h, u, c, 4, 1.2)
                + mids
                    .map(([x, y, dx, dy]) => `<path d="M${x} ${y} L${x + dx * k} ${y + dy * k}" stroke="${c}" stroke-width="${1.6 * u}" stroke-linecap="round" fill="none"/>`)
                    .join('');
        },
    },

    /* — shaped edges ------------------------------------------------ */
    {
        id: 'wave',
        inset: 0.15,
        build: (w, h, u, c) =>
            `<path d="${aroundBox(w, h, u, 5, (x1, y1, x2, y2) => waveLine(x1, y1, x2, y2, 2 * u, 8 * u))}"
                fill="none" stroke="${c}" stroke-width="${1.1 * u}" stroke-linejoin="round"/>`,
    },
    {
        id: 'zigzag',
        inset: 0.15,
        build: (w, h, u, c) =>
            `<path d="${aroundBox(w, h, u, 5, (x1, y1, x2, y2) => zigzagLine(x1, y1, x2, y2, 1.8 * u, 5 * u))}"
                fill="none" stroke="${c}" stroke-width="${1 * u}" stroke-linejoin="round"/>`,
    },
    {
        id: 'scallop',
        inset: 0.15,
        build: (w, h, u, c) =>
            `<path d="${aroundBox(w, h, u, 5, (x1, y1, x2, y2) => scallopLine(x1, y1, x2, y2, 3 * u))}"
                fill="none" stroke="${c}" stroke-width="${1 * u}" stroke-linejoin="round"/>`
            + rule(w, h, u, c, 9, 0.45),
    },
    {
        id: 'waveDouble',
        inset: 0.17,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 3.5, 1.2)
            + `<path d="${aroundBox(w, h, u, 8, (x1, y1, x2, y2) => waveLine(x1, y1, x2, y2, 1.6 * u, 7 * u))}"
                fill="none" stroke="${c}" stroke-width="${0.7 * u}" stroke-linejoin="round"/>`,
    },

    /* — ornamental --------------------------------------------------- */
    {
        id: 'deco',
        inset: 0.17,
        build: (w, h, u, c) => rule(w, h, u, c, 3, 0.6) + fourCorners(w, h, u, decoCorner(c), 22, 3),
    },
    {
        id: 'ornate',
        inset: 0.17,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 5, 0.5)
            + fourCorners(w, h, u, ornateCorner(c))
            + topBottomSprigs(w, h, u, ornateSprig(c)),
    },
    {
        id: 'ornateLight',
        inset: 0.15,
        build: (w, h, u, c) => rule(w, h, u, c, 4, 0.45) + fourCorners(w, h, u, ornateCorner(c), 19, 2),
    },
    {
        id: 'floral',
        inset: 0.17,
        build: (w, h, u, c) => rule(w, h, u, c, 4.5, 0.5) + fourCorners(w, h, u, floralCorner(c), 24, 2.5),
    },
    {
        id: 'laurel',
        inset: 0.16,
        build: (w, h, u, c) =>
            rule(w, h, u, c, 4, 1.1) + topBottomSprigs(w, h, u, laurelSprig(c), 30, 4),
    },
    {
        id: 'certificate',
        inset: 0.19,
        build: (w, h, u, c) => {
            const s = 3.2 * u;
            return rule(w, h, u, c, 3, 1.8)
                + rule(w, h, u, c, 6, 0.5)
                + corners(w, h, u, 4.5)
                    .map(([x, y]) => `<rect x="${x - s / 2}" y="${y - s / 2}" width="${s}" height="${s}" fill="${c}" transform="rotate(45 ${x} ${y})"/>`)
                    .join('')
                + fourCorners(w, h, u, ornateCorner(c), 20, 7.5)
                + topBottomSprigs(w, h, u, ornateSprig(c), 22, 6.6);
        },
    },
];

const FRAME_BY_ID = new Map(NOTICE_FRAMES.map((frame) => [frame.id, frame]));

export function getFrameById(id) {
    return FRAME_BY_ID.get(id) || FRAME_BY_ID.get('double');
}

/**
 * SVG document for one frame at a given pixel size.
 * @returns {string} markup — empty-bodied SVG for the 'none' frame
 */
export function buildFrameSvg(id, width, height, color = '#111111') {
    const frame = getFrameById(id);
    const u = Math.min(width, height) / 100;
    const body = frame.build(width, height, u, color);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${body}</svg>`;
}

/** `buildFrameSvg` output as a data URL, ready for an <img> or drawImage. */
export function frameSvgDataUrl(id, width, height, color) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(buildFrameSvg(id, width, height, color))}`;
}
